import {
  ReadonlySignal,
  Signal,
  useComputed,
  useSignalEffect,
} from '@preact/signals-react';
import produce from 'immer';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import { AiComponentJsVariable, AiManifestComponent } from '@/pkg/ai';
import { AiOriginAction, CompoundChange } from '@/pkg/sdk';
import { capitalize } from '@/utils/types';

export function useVisualManifestBuilder(
  change: Signal<EditorDeclarativeBlock>,
  parsedHtml: Signal<HTMLDivElement>,
  parsedCss: ReadonlySignal<CSSRuleList>,
  parsedJs: ReadonlySignal<string>,
  jsVariables: ReadonlySignal<AiComponentJsVariable[]>,
) {
  const aiOrigin = useComputed(() => {
    const block = change.value.block.value as CompoundChange;
    return block.aiOrigin;
  });
  const extractedHtmlElements = useComputed(() => {
    const richTextInputs = Array.from(
      parsedHtml.value.querySelectorAll(`[richtext],[richText]`),
    ).filter((e) => !e.classList.contains(`editor-placeholder`));

    const plainTextInputs = Array.from(
      parsedHtml.value.querySelectorAll(`[plaintext],[plainText]`),
    ).filter((e) => !e.classList.contains(`editor-placeholder`));

    const videos = Array.from(
      parsedHtml.value.querySelectorAll(`video`),
    ).filter((e) => !e.classList.contains(`editor-placeholder`));

    const images = Array.from(
      parsedHtml.value.querySelectorAll(`img[src]`),
    ).filter((e) => !e.classList.contains(`editor-placeholder`));

    const links = Array.from(parsedHtml.value.querySelectorAll(`a`)).filter(
      (e) => !e.classList.contains(`editor-placeholder`),
    );

    const out: AiManifestComponent[] = [];
    richTextInputs?.forEach((rt) => {
      const name = findName(rt as HTMLElement);
      out.push({
        type: `html-content`,
        name: `${name} Content`,
        description: ``,
        richText: true,
        selector: getCssSelector(rt),
        node: rt.nodeName,
        value: rt?.innerHTML || ``,
        group: name,
      });
    });

    plainTextInputs?.forEach((pt) => {
      const name = findName(pt as HTMLElement);
      out.push({
        type: `html-content`,
        name: `${name} Content`,
        description: ``,
        richText: false,
        selector: getCssSelector(pt),
        node: pt.nodeName,
        value: pt.textContent,
        group: name,
      });
    });

    images?.forEach((img) => {
      const name = findName(img as HTMLElement);
      out.push({
        type: `html-attr`,
        name: `${name} Image`,
        description: ``,
        selector: getCssSelector(img),
        node: `img`,
        attr: `src`,
        value: img.getAttribute(`src`),
        group: name,
      });
    });

    links?.forEach((a) => {
      const name = findName(a as HTMLElement);
      out.push({
        type: `html-attr`,
        name: `${name} Link`,
        description: ``,
        selector: getCssSelector(a),
        node: `a`,
        attr: `href`,
        value: a.getAttribute(`href`) || `#`,
        group: name,
      });
    });

    videos?.forEach((vid) => {
      const name = findName(vid as HTMLElement);
      const componentName = name.endsWith(`Video`)
        ? `${name} Content`
        : `${name} Video Content`;
      out.push({
        type: `html-video`,
        name: componentName,
        description: ``,
        selector: getCssSelector(vid),
        inlineSource: vid.hasAttribute(`src`),
        node: `video`,
        group: name,
      });
    });

    return out;
  });

  const templates = useComputed(() =>
    Array.from(parsedHtml.value.querySelectorAll(`template`)),
  );

  const extractedCssRules = useComputed(() => {
    if (aiOrigin.value === AiOriginAction.SECTION_MODIFIER) {
      return extractSectionEditorCssRules(change.peek());
    }

    const out: AiManifestComponent[] = [];
    Array.from(parsedCss.value).forEach((rule) => {
      const styleRule = rule as CSSStyleRule;
      let elem = parsedHtml.value.querySelector(styleRule.selectorText);
      if (!elem) {
        const newSelector = styleRule?.selectorText
          ?.replaceAll(change.peek().editorSelector, ``)
          ?.trim();

        if (newSelector?.includes(`animations`)) return;

        if (templates.value?.length > 0) {
          templates.value.forEach((template) => {
            elem = template.content.querySelector(newSelector);
          });
        }

        const parts = cleanCssSelectorForComparison(newSelector);
        const isReferencedInJs = parts
          .filter((part) => !part.includes(`animations`))
          .some((part) => parsedJs.value.includes(part.replace(`.`, ``)));
        if (isReferencedInJs) {
          elem = document.createElement(`div`);
          elem.setAttribute(`name`, cssSelectorToName(newSelector));
        }

        if (!elem) return;
      }
      if (elem.getAttribute(`name`) === null) return;

      out.push({
        type: `css-rule`,
        name: `${elem.getAttribute(`name`) || styleRule.selectorText} Style`,
        description: ``,
        selector: styleRule.selectorText,
        group: elem.getAttribute(`name`),
      });
    });
    return out;
  });

  const extractedJsVariables = useComputed(() =>
    jsVariables.value.map(
      (variable) =>
        ({
          type: `js-variable`,
          name: formatName(variable.name),
          description: ``,
          selector: variable.variable,
          variableType: variable.variableType,
          value: variable.value,
          variable: variable.variable,
          group: `General Settings`,
        } as AiComponentJsVariable),
    ),
  );

  useSignalEffect(() => {
    change.value = produce(change.peek(), (draft) => {
      const block = draft.block.value as CompoundChange;
      const components = [
        ...extractedHtmlElements.value,
        ...extractedCssRules.value,
        ...extractedJsVariables.value,
      ].filter(
        (comp, idx, arr) =>
          arr.findIndex(
            (c) =>
              c.type === comp.type &&
              c.selector === comp.selector &&
              c.name === comp.name,
          ) === idx,
      );

      const generalSettings = components.filter(
        (c) => c.group === `General Settings`,
      );
      const otherGroups = components.filter(
        (c) => c.group !== `General Settings`,
      );

      // Find the group of the first html-video component, if any
      const videoComponent = components.find((c) => c.type === `html-video`);
      const videoGroup = videoComponent?.group;

      // Build orderedGroups, pulling videoGroup right after General Settings if present
      let orderedGroups: string[] = [];
      otherGroups.forEach((c) => {
        if (c.group && !orderedGroups.includes(c.group)) {
          orderedGroups.push(c.group);
        }
      });

      if (videoGroup && orderedGroups.includes(videoGroup)) {
        // Remove videoGroup from its current position
        orderedGroups = orderedGroups.filter((g) => g !== videoGroup);
        // Insert videoGroup right after General Settings
        orderedGroups = [videoGroup, ...orderedGroups];
      }

      const sortedComponents = [
        ...generalSettings,
        ...(videoGroup
          ? otherGroups.filter((c) => c.group === videoGroup)
          : []),
        ...orderedGroups
          .filter((g) => !videoGroup || g !== videoGroup)
          .flatMap((group) => otherGroups.filter((c) => c.group === group)),
      ];

      block.editManifest = {
        components: sortedComponents,
      };
    });
  });

  const isFirstInGroup = (component: AiManifestComponent): boolean => {
    const block = change.value.block.value as CompoundChange;
    if (!block.editManifest?.components) return false;
    const { components } = block.editManifest;
    const index = components.indexOf(component);
    if (index === -1) return false;
    if (index === 0) return true;
    if (component.group === undefined) return false;
    for (let i = index - 1; i >= 0; i -= 1) {
      if (components[i].group === component.group) {
        return false;
      }
      if (components[i].group !== component.group) {
        return true;
      }
    }
    return true;
  };

  return {
    isFirstInGroup,
  };
}

function findName(el: HTMLElement | undefined): string {
  let current = el;
  let name = ``;
  while (current) {
    const attr = current.getAttribute?.(`name`);
    if (attr) {
      name = capitalize(attr);
      // Find all elements with the same name in the parent context
      const parent = current.parentElement || current.ownerDocument;
      const allWithName = parent?.querySelectorAll?.(`[name="${attr}"]`) ?? [];
      if (allWithName.length > 1) {
        // Find the index of the current node among siblings with the same name
        let index = 0;
        for (let i = 0; i < allWithName.length; i += 1) {
          if (allWithName[i] === current) {
            index = i + 1;
            break;
          }
        }
        name += ` #${index}`;
      }
      return name;
    }
    current = current.parentElement as HTMLElement | null;
  }
  return ``;
}

export function getCssSelector(el: Element): string {
  if (!el || el.nodeType !== 1) return ``;

  const parts = [];
  while (el && el.nodeType === 1) {
    let selector = el.tagName.toLowerCase();

    // If the element has an id, use it and stop (unique in the document)
    if (el.id) {
      const id = window.CSS && CSS.escape ? CSS.escape(el.id) : el.id;
      parts.unshift(`${selector}#${id}`);
      break;
    }

    // Append all classes (order preserved)
    if (el.classList && el.classList.length) {
      const classes = Array.from(el.classList)
        .map((cls) =>
          window.CSS && CSS.escape ? `.${CSS.escape(cls)}` : `.${cls}`,
        )
        .join(``);
      selector += classes;
    }

    // Count previous siblings of the same tag name for :nth-of-type
    let prevSame = 0;
    let prev = el.previousElementSibling;
    while (prev) {
      if (prev.tagName === el.tagName) prevSame += 1;
      prev = prev.previousElementSibling;
    }
    if (prevSame > 0) {
      selector += `:nth-of-type(${prevSame + 1})`;
    }

    parts.unshift(selector);
    el = el.parentElement; // up the tree (stops before document node)
  }

  return parts.join(` > `);
}

function formatName(input: string): string {
  // Remove leading underscores and prefix like "_vslyExposed_"
  const cleaned = input.replace(/^_?vslyExposed_/, ``);

  // Split into words based on camelCase or underscores
  const words = cleaned
    .replace(/([a-z])([A-Z])/g, `$1 $2`) // split camelCase
    .replace(/_/g, ` `) // handle underscores if any
    .split(` `);

  // Capitalize each word
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(` `);
}

function cssSelectorToName(selector: string) {
  return selector
    ?.replace(/_loomi_addon_\d{13}/g, ``) // Remove _loomi_addon references
    ?.replace(`::`, ` `)
    ?.replace(`:`, ` `)
    ?.replace(/^[.#]/, ``) // remove leading . or #
    ?.replace(/\s*>\s*/g, ` `) // replace child combinators with space
    ?.replace(/[\[\]="']/g, ``) // remove attribute syntax
    ?.replace(/[^a-zA-Z0-9]+/g, ` `) // replace non-alphanumeric chars with spaces
    ?.trim()
    ?.split(/\s+/)
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    ?.join(` `);
}

/**
 * Cleans a CSS selector so it can be used to check JS references.
 * Converts it into a normalized array of class/id-like identifiers.
 *
 * Examples:
 *  cleanCssSelectorForComparison('.btn.primary:hover')
 *   -> ['.btn', '.primary']
 *
 *  cleanCssSelectorForComparison('#main > .header__title span')
 *   -> ['.main', '.header__title']
 */
export function cleanCssSelectorForComparison(selector: string): string[] {
  if (!selector || typeof selector !== `string`) return [];

  const cleaned = selector
    // Remove combinators like >, +, ~ and extra spaces
    .replace(/\s*([>+~])\s*/g, ` `)
    // Remove attribute selectors [type="text"]
    .replace(/\[.*?\]/g, ``)
    // Remove pseudo-classes and pseudo-elements (:hover, ::before)
    .replace(/:{1,2}[a-zA-Z0-9()_-]+/g, ``)
    // Replace multiple spaces or commas with single space
    .replace(/[\s,]+/g, ` `)
    // Remove quotes and trim
    .replace(/["']/g, ``)
    .trim();

  // Split selector into parts
  const parts = cleaned.split(/\s+/).flatMap((part) => {
    // Remove HTML tag names before class or id
    const withoutTag = part.replace(/^[a-zA-Z]+(?=[.#])/g, ``);

    // Break down combined selectors like `.btn.primary` → ['.btn', '.primary']
    const matches = withoutTag.match(/[#.][a-zA-Z0-9_-]+/g);
    return matches || [];
  });

  // Normalize: convert IDs to `.idName` for consistent comparison
  const normalized = parts.map((p) =>
    p.startsWith(`#`) ? `.${p.slice(1)}` : p,
  );

  // Deduplicate
  return Array.from(new Set(normalized));
}

/**
 * Parses CSS rules formatted with @name comments (section editor format)
 * Example input:
 *   /* @name: Container Spacing *\/
 *   /* Add generous spacing *\/
 *   .justify-start .rich-text__wrapper { ... }
 */
function extractSectionEditorCssRules(
  change: EditorDeclarativeBlock,
): AiManifestComponent[] {
  const block = change.block.value as CompoundChange;
  const cssText = block.css || ``;
  const out: AiManifestComponent[] = [];

  if (!cssText || cssText.trim().length === 0) {
    return out;
  }

  // Split CSS into blocks, each potentially having a @name comment
  // Pattern: /* @name: Name */ followed by optional comments and a CSS rule
  const lines = cssText.split(`\n`);
  let currentName: string | null = null;
  let currentSelector: string | null = null;
  let insideRule = false;
  let braceCount = 0;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i].trim();

    // Check for @name comment
    const nameMatch = line.match(/\/\*\s*@name:\s*([^*]+)\*\//);
    if (nameMatch) {
      currentName = nameMatch[1].trim();
    } else if (line.startsWith(`/*`) || line.startsWith(`*`) || line === `*/`) {
      // Skip other comments
    } else if (line.length === 0) {
      // Skip empty lines
    } else if (line.includes(`{`)) {
      // Detect selector (line ending with { or containing {)
      insideRule = true;
      braceCount += (line.match(/{/g) || []).length;
      braceCount -= (line.match(/}/g) || []).length;

      // Extract selector (everything before the first {)
      const selectorPart = line.split(`{`)[0].trim();
      if (selectorPart) {
        currentSelector = selectorPart;
      }
    } else if (insideRule) {
      // We're inside a rule, count braces
      braceCount += (line.match(/{/g) || []).length;
      braceCount -= (line.match(/}/g) || []).length;
    }

    // Check if rule is complete
    if (insideRule && braceCount === 0) {
      // Rule is complete, add to manifest if we have both name and selector
      if (currentName && currentSelector) {
        out.push({
          type: `css-rule`,
          name: `${currentName}`,
          description: ``,
          selector: currentSelector,
          group: currentName,
        });
      }

      // Reset for next rule
      currentName = null;
      currentSelector = null;
      insideRule = false;
    }
  }

  return out;
}
